/**
 * Compliance attributes shared by the three frontends. Mirrors
 * functions/src/product-compliance.ts: DFARS, domestic melt, single heat,
 * MTR available, and the carbon footprint (kg CO2e per kg).
 */

export const COMPLIANCE_FLAGS = [
  "dfarsCompliant",
  "domesticMelt",
  "singleHeat",
  "mtrAvailable",
] as const;

export type ComplianceFlag = (typeof COMPLIANCE_FLAGS)[number];

export const COMPLIANCE_FLAG_LABELS: Record<ComplianceFlag, string> = {
  dfarsCompliant: "DFARS compliant",
  domesticMelt: "Domestic melt (USA)",
  singleHeat: "Single heat",
  mtrAvailable: "MTR / certs available",
};

/** Short help text shown next to each flag in the editors. */
export const COMPLIANCE_FLAG_HELP: Record<ComplianceFlag, string> = {
  dfarsCompliant:
    "Specialty metal melted in the USA or a qualifying country (DFARS 252.225-7014).",
  domesticMelt: "Melted and produced in the United States.",
  singleHeat: "The lot can ship from one heat / melt lot.",
  mtrAvailable: "A mill test report (certs) ships with the material.",
};

export type ComplianceValues = {
  dfarsCompliant?: boolean | null;
  domesticMelt?: boolean | null;
  singleHeat?: boolean | null;
  mtrAvailable?: boolean | null;
  co2eKgPerKg?: number | null;
  co2eSource?: string | null;
};

/** Labels of the attributes an item states true, in a stable order. */
export const complianceLabels = (
  item: { [key: string]: any } | null | undefined,
): string[] =>
  COMPLIANCE_FLAGS.filter((flag) => item?.[flag] === true).map(
    (flag) => COMPLIANCE_FLAG_LABELS[flag],
  );

/** "8.2 kg CO2e/kg (vendor EPD 2025)" or "" when no figure. */
export const formatCo2e = (
  item: { co2eKgPerKg?: number | null; co2eSource?: string | null } | null | undefined,
): string => {
  const value = item?.co2eKgPerKg;
  if (typeof value !== "number" || !Number.isFinite(value)) return "";
  const source = String(item?.co2eSource || "").trim();
  return `${value} kg CO2e/kg${source ? ` (${source})` : ""}`;
};

/** Tri-state select value <-> boolean|null. */
export const flagToSelect = (value: boolean | null | undefined): string =>
  value === true ? "yes" : value === false ? "no" : "";
export const selectToFlag = (value: string): boolean | null =>
  value === "yes" ? true : value === "no" ? false : null;
